/**
 * Enumerations shared by the database, API, web app and desktop agent.
 * The database mirrors these with CHECK constraints (see apps/api/migrations).
 */

export const ROLE_KEYS = ['admin', 'manager', 'employee'] as const;
export type RoleKey = (typeof ROLE_KEYS)[number];

export const EMPLOYMENT_TYPES = [
  'full_time',
  'part_time',
  'contractor',
  'intern',
  'temporary',
] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  contractor: 'Contractor',
  intern: 'Intern',
  temporary: 'Temporary',
};

export const EMPLOYMENT_STATUSES = ['active', 'probation', 'suspended', 'terminated'] as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUSES)[number];

export const EMPLOYMENT_STATUS_LABELS: Record<EmploymentStatus, string> = {
  active: 'Active',
  probation: 'Probation',
  suspended: 'Suspended',
  terminated: 'Terminated',
};

export const WORK_ARRANGEMENTS = ['office', 'remote', 'hybrid'] as const;
export type WorkArrangement = (typeof WORK_ARRANGEMENTS)[number];
export const WORK_ARRANGEMENT_LABELS: Record<WorkArrangement, string> = {
  office: 'Office',
  remote: 'Remote',
  hybrid: 'Hybrid',
};

/** ISO weekday numbers: 1 = Monday … 7 = Sunday (same as Luxon). */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
export type Weekday = (typeof WEEKDAYS)[number];
export const WEEKDAY_LABELS: Record<Weekday, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

/**
 * How a day's worked time is derived when there are several punches.
 * - first_last: first sign-in → last sign-out (company default)
 * - sum_intervals: sum of each in→out pair
 */
export const ATTENDANCE_CALC_MODES = ['first_last', 'sum_intervals'] as const;
export type AttendanceCalcMode = (typeof ATTENDANCE_CALC_MODES)[number];

export const LEAVE_REQUEST_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'] as const;
export type LeaveRequestStatus = (typeof LEAVE_REQUEST_STATUSES)[number];

export const NOTIFICATION_TYPES = [
  'leave.submitted',
  'leave.approved',
  'leave.rejected',
  'leave.cancelled',
  'leave.balance_changed',
  'attendance.missing_clock_out',
  'attendance.issue',
  'report.generated',
  'period.ended',
  'productivity.daily',
  'account.created',
  'account.password_reset',
  'policy.updated',
  'system',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export const ACCRUAL_METHODS = ['upfront', 'monthly'] as const;
export type AccrualMethod = (typeof ACCRUAL_METHODS)[number];

export const LEDGER_KINDS = ['entitlement', 'entitlement_change', 'carry_over', 'adjustment', 'usage', 'reversal'] as const;
export type LedgerKind = (typeof LEDGER_KINDS)[number];
export const LEDGER_KIND_LABELS: Record<LedgerKind, string> = {
  entitlement: 'Yearly entitlement',
  entitlement_change: 'Allowance changed',
  carry_over: 'Carried over',
  adjustment: 'Manual adjustment',
  usage: 'Leave taken',
  reversal: 'Leave returned',
};

export const LEAVE_STATUS_LABELS: Record<LeaveRequestStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};
